import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IEmployeesState } from "./types";
import { IIDAStore } from "../types";
import { Employee } from "../../models/employees";
import { employeeService } from "../../services/Employees.service";

export const getInitialState = (): IEmployeesState => {
  return {
    searching: false,
    employees: [],
    highestSearchDate: new Date(1900, 1, 1),
    lowestSearchDate: new Date(2500, 1, 1)
  };
};

const namespaced = true;

export const getters: GetterTree<IEmployeesState, IIDAStore> = {
  GetEmployeeById: (state) => (id: number) => {
    return state.employees.find((e) => e.id === id);
  },

  GetEmployees: (state) => () => {
    return state.employees;
  }
};

export const actions: ActionTree<IEmployeesState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchEmployees({ commit, state }, query: { includeHidden: boolean }): Promise<Employee[]> {
    return new Promise((resolve, reject) => {
      if (state.employees.length === 0) {
        try {
          employeeService.fetchEmployees().then((employees) => {
            commit("updateEmployees", { employees });
            if (query.includeHidden) {
              resolve(employees);
            } else {
              resolve(employees.filter((e) => e.is_hidden === false));
            }
          });
        } catch (error) {
          return reject(error);
        }
      } else if (query.includeHidden) {
        resolve(state.employees);
      } else {
        resolve(state.employees.filter((e) => e.is_hidden === false));
      }
    });
  },
  createEmployee({ commit }, employee: Employee): Promise<Employee> {
    return new Promise((resolve, reject) => {
      employeeService
        .createEmployee(employee.convertToPostObject())
        .then((newEmployee) => {
          commit("insertEmployee", newEmployee);
          resolve(newEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmployee({ commit }, employee: Employee): Promise<Employee> {
    return new Promise((resolve, reject) => {
      employeeService
        .updateEmployee(employee)
        .then((updatedEmployee) => {
          commit("updateEmployee", updatedEmployee);
          resolve(updatedEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmployeeContactPerson({ commit }, employee: Employee): Promise<Employee> {
    return new Promise((resolve, reject) => {
      employeeService
        .updateEmployeeContactPerson(employee)
        .then((updatedEmployee) => {
          commit("updateEmployee", updatedEmployee);
          resolve(updatedEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEmployee({ commit }, employeeId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      employeeService
        .deleteEmployee(employeeId)
        .then((response) => {
          commit("deleteEmployee", employeeId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IEmployeesState> = {
  reset(state: IEmployeesState) {
    Object.assign(state, getInitialState());
  },
  insertEmployee(state: IEmployeesState, employee: Employee) {
    state.employees.push(employee);
  },
  updateEmployee(state: IEmployeesState, employee: Employee) {
    const index = state.employees.findIndex((d) => d.id === employee.id);
    state.employees.splice(index, 1);
    state.employees.push(employee);
  },
  deleteEmployee(state: IEmployeesState, employeeId: number) {
    const index = state.employees.findIndex((d) => d.id === employeeId);
    state.employees.splice(index, 1);
  },
  updateEmployees(state: IEmployeesState, payload: { employees: Employee[] }) {
    state.employees = [
      ...state.employees,
      ...payload.employees.filter(
        (newEmployee) => !state.employees.find((existing) => existing.equals(newEmployee))
      )
    ];
  }
};

export const employeesStoreModule: Module<IEmployeesState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
